import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import axios from 'axios';
import API_URL from '../../config';
import { getEnterpriseVehicleMailto } from '../../config/pricingPublic';
import '../../App.css';

const AddVehicleModal = ({ isOpen, onClose, onSuccess, vehicle = null }) => {
  const [formData, setFormData] = useState({
    vin: vehicle?.vin || '',
    year: vehicle?.year || new Date().getFullYear(),
    make: vehicle?.make || '',
    model: vehicle?.model || '',
    licensePlate: vehicle?.licensePlate || '',
    licensePlateState: vehicle?.licensePlateState || '',
    nickname: vehicle?.nickname || '',
    lastInspectionDate: vehicle?.lastInspectionDate
      ? new Date(vehicle.lastInspectionDate).toISOString().split('T')[0]
      : '',
    registrationExpirationDate: vehicle?.registrationExpirationDate
      ? new Date(vehicle.registrationExpirationDate).toISOString().split('T')[0]
      : '',
    notes: vehicle?.notes || '',
  });

  const [inspectionFile, setInspectionFile] = useState(null);
  const [registrationFile, setRegistrationFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [limitError, setLimitError] = useState('');
  const [limitEnterprise, setLimitEnterprise] = useState(false);

  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  ];

  // Through next year so the default (current calendar year) is always a real option.
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1979 + 1 }, (_, i) => 1980 + i);

  const commonMakes = [
    'Freightliner',
    'Peterbilt',
    'Kenworth',
    'Volvo',
    'Mack',
    'International',
    'Western Star',
    'Caterpillar',
    'Cummins',
    'Detroit Diesel',
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // <select> values are always strings; the API's @IsNumber() rejects "2024".
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'year' ? Number(value) : value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // VIN validation
    if (name === 'vin') {
      const vinUpper = value.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
      if (vinUpper.length <= 17) {
        setFormData((prev) => ({ ...prev, vin: vinUpper }));
      }
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'inspection') {
        setInspectionFile(file);
      } else {
        setRegistrationFile(file);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vin || formData.vin.length !== 17) {
      newErrors.vin = 'VIN must be exactly 17 characters';
    }

    if (!formData.year || Number.isNaN(Number(formData.year))) {
      newErrors.year = 'Year is required';
    }

    if (!formData.make) {
      newErrors.make = 'Make is required';
    }

    if (!formData.model) {
      newErrors.model = 'Model is required';
    }

    if (!formData.licensePlate) {
      newErrors.licensePlate = 'License plate is required';
    }

    if (!formData.licensePlateState) {
      newErrors.licensePlateState = 'State is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setLimitError('');
    setLimitEnterprise(false);

    try {
      const token = localStorage.getItem('token');
      let vehicleId = vehicle?.id;

      // Select/change handlers store strings; optional dates are '' when unused.
      // The DTO wants year as a number and omits empty ISO date fields.
      const payload = {
        vin: formData.vin,
        year: Number(formData.year),
        make: formData.make,
        model: formData.model,
        licensePlate: formData.licensePlate,
        licensePlateState: formData.licensePlateState,
      };
      if (formData.nickname.trim()) payload.nickname = formData.nickname.trim();
      if (formData.notes.trim()) payload.notes = formData.notes.trim();
      if (formData.lastInspectionDate) payload.lastInspectionDate = formData.lastInspectionDate;
      if (formData.registrationExpirationDate) {
        payload.registrationExpirationDate = formData.registrationExpirationDate;
      }

      // Create or update vehicle
      if (vehicle) {
        await axios.put(
          `${API_URL}/api/vehicles/${vehicle.id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        vehicleId = vehicle.id;
      } else {
        const response = await axios.post(
          `${API_URL}/api/vehicles`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        vehicleId = response.data.id;
      }

      // Upload inspection certificate if provided
      if (inspectionFile && vehicleId) {
        const inspectionFormData = new FormData();
        inspectionFormData.append('file', inspectionFile);
        await axios.post(
          `${API_URL}/api/vehicles/${vehicleId}/inspection`,
          inspectionFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      }

      // Upload registration document if provided
      if (registrationFile && vehicleId) {
        const registrationFormData = new FormData();
        registrationFormData.append('file', registrationFile);
        await axios.post(
          `${API_URL}/api/vehicles/${vehicleId}/registration`,
          registrationFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error saving vehicle:', err);
      const data = err.response?.data;
      if (data?.limitReached) {
        const msg =
          typeof data.message === 'string'
            ? data.message
            : 'Vehicle limit reached for your current plan. Upgrade to add more vehicles.';
        setLimitError(msg);
        setLimitEnterprise(data.enterpriseContact === true);
        return;
      }
      const msg =
        (typeof data?.message === 'string' && data.message) ||
        (Array.isArray(data?.message) ? data.message.join(', ') : null) ||
        'Failed to save vehicle. Please try again.';
      alert(typeof msg === 'string' ? msg : 'Failed to save vehicle. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedDown: (eventData) => {
      if (eventData.first) {
        onClose();
      }
    },
    trackMouse: true,
    delta: 50,
  });

  if (!isOpen) return null;

  return (
    <div className="modal-overlay fleet-edit-modal-overlay" onClick={onClose}>
      <div
        className="vehicle-modal fleet-edit-modal"
        onClick={(e) => e.stopPropagation()}
        {...swipeHandlers}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            {vehicle ? 'Edit Vehicle' : 'Add Vehicle'}
          </h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="vehicle-form">
          {limitError ? (
            <div className="drivers-limit-error" role="alert">
              <p>{limitError}</p>
              {limitEnterprise ? (
                <a href={getEnterpriseVehicleMailto()}>Contact us about Enterprise</a>
              ) : (
                <Link to="/pricing">Upgrade your plan</Link>
              )}
            </div>
          ) : null}
          <div className="form-section">
            <h3 className="form-section-title">Vehicle Information</h3>
            <div className="form-section-content">
              <div className="input-group">
                <input
                  type="text"
                  name="vin"
                  value={formData.vin}
                  onChange={handleInputChange}
                  className={`form-input ${errors.vin ? 'error' : ''}`}
                  placeholder="VIN (17 characters)"
                  maxLength={17}
                  required
                />
                <label className="form-label">
                  VIN <span className="required">*</span>
                  {formData.vin.length > 0 && (
                    <span className="char-count">{formData.vin.length}/17</span>
                  )}
                </label>
                {errors.vin && <span className="error-message">{errors.vin}</span>}
              </div>

              <div className="form-row">
                <div className="input-group">
                  <select
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    className={`form-input ${errors.year ? 'error' : ''}`}
                    required
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <label className="form-label">
                    Year <span className="required">*</span>
                  </label>
                  {errors.year && <span className="error-message">{errors.year}</span>}
                </div>

                <div className="input-group">
                  <input
                    type="text"
                    name="make"
                    value={formData.make}
                    onChange={handleInputChange}
                    list="makes"
                    className={`form-input ${errors.make ? 'error' : ''}`}
                    placeholder="Make"
                    required
                  />
                  <datalist id="makes">
                    {commonMakes.map((make) => (
                      <option key={make} value={make} />
                    ))}
                  </datalist>
                  <label className="form-label">
                    Make <span className="required">*</span>
                  </label>
                  {errors.make && <span className="error-message">{errors.make}</span>}
                </div>

                <div className="input-group">
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    className={`form-input ${errors.model ? 'error' : ''}`}
                    placeholder="Model"
                    required
                  />
                  <label className="form-label">
                    Model <span className="required">*</span>
                  </label>
                  {errors.model && <span className="error-message">{errors.model}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="input-group">
                  <input
                    type="text"
                    name="licensePlate"
                    value={formData.licensePlate}
                    onChange={handleInputChange}
                    className={`form-input ${errors.licensePlate ? 'error' : ''}`}
                    placeholder="License Plate"
                    required
                  />
                  <label className="form-label">
                    License Plate <span className="required">*</span>
                  </label>
                  {errors.licensePlate && (
                    <span className="error-message">{errors.licensePlate}</span>
                  )}
                </div>

                <div className="input-group">
                  <select
                    name="licensePlateState"
                    value={formData.licensePlateState}
                    onChange={handleInputChange}
                    className={`form-input ${errors.licensePlateState ? 'error' : ''}`}
                    required
                  >
                    <option value="">Select State</option>
                    {usStates.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                  <label className="form-label">
                    State <span className="required">*</span>
                  </label>
                  {errors.licensePlateState && (
                    <span className="error-message">{errors.licensePlateState}</span>
                  )}
                </div>
              </div>

              <div className="input-group">
                <input
                  type="text"
                  name="nickname"
                  value={formData.nickname}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Nickname (optional)"
                />
                <label className="form-label">Nickname</label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Inspection & Registration</h3>
            <div className="form-section-content">
              <div className="form-row">
                <div className="input-group">
                  <input
                    type="date"
                    name="lastInspectionDate"
                    value={formData.lastInspectionDate}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <label className="form-label">Last Inspection Date</label>
                </div>

                <div className="input-group">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'inspection')}
                    className="form-input file-input"
                  />
                  <label className="form-label">
                    Inspection Certificate
                    {inspectionFile && (
                      <span className="file-name">: {inspectionFile.name}</span>
                    )}
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="input-group">
                  <input
                    type="date"
                    name="registrationExpirationDate"
                    value={formData.registrationExpirationDate}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                  <label className="form-label">Registration Expiration</label>
                </div>

                <div className="input-group">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'registration')}
                    className="form-input file-input"
                  />
                  <label className="form-label">
                    Registration Document
                    {registrationFile && (
                      <span className="file-name">: {registrationFile.name}</span>
                    )}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Additional Information</h3>
            <div className="form-section-content">
              <div className="input-group">
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="form-input textarea"
                  placeholder="Notes (optional)"
                  rows={4}
                />
                <label className="form-label">Notes</label>
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="modal-button cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="modal-button save"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : vehicle ? 'Update Vehicle' : 'Add Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddVehicleModal;

